import {useContext, useEffect, useRef, useState} from "react";
import AppContext from "../context/AppContext";
import {useApi} from "../api";
import {getVideoLink} from "../lib/static";
import ContextMenu, {ContextMenuItem} from "./ContextMenu";
import {useNavigate} from "react-router-dom";
import './ContinueWatching.scss'

type HistoryItem = { file: VideoFile, position: number }
type CtxMenu = { x: number, y: number, item: HistoryItem }

export default () => {
    const api = useApi()
    const navigate = useNavigate()
    const { setSelectedVideo, hasFileManager } = useContext(AppContext)
    const [items, setItems] = useState<HistoryItem[]>([])
    const [contextMenu, setContextMenu] = useState<CtxMenu | null>(null)

    const load = () => api.getWatchHistory().then(setItems).catch(() => {})

    useEffect(() => {
        load()
        window.addEventListener('memo:history-changed', load)
        return () => window.removeEventListener('memo:history-changed', load)
    }, [])

    if (items.length === 0) return null

    const open = (item: HistoryItem) => {
        setSelectedVideo(item.file)
        window.history.pushState(null, '', getVideoLink(item.file.hash))
    }

    const buildMenuItems = (item: HistoryItem): ContextMenuItem[] => {
        const menuItems: ContextMenuItem[] = []

        if (item.file.directory?.hash) {
            menuItems.push({
                label: 'Go to folder',
                onClick: () => {
                    setSelectedVideo(null)
                    navigate(`/dir/${item.file.directory.hash}`)
                },
            })
        }

        if (hasFileManager) {
            menuItems.push({
                label: 'Open in file manager',
                onClick: () => api.openInFileManager(item.file.hash),
            })
        }

        menuItems.push({
            label: 'Remove from history',
            onClick: () => {
                api.setWatchPosition(item.file.hash, 0).then(() => {
                    window.dispatchEvent(new CustomEvent('memo:history-changed'))
                })
            },
        })

        return menuItems
    }

    return (
        <div className="continue-watching">
            <div className="cw-header">Continue watching</div>
            <div className="cw-scroll">
                {items.map(item => {
                    const pct = item.file.meta?.duration
                        ? Math.min(100, (item.position / item.file.meta.duration) * 100)
                        : 0
                    return (
                        <CwCard
                            key={item.file.hash}
                            item={item}
                            pct={pct}
                            onClick={() => open(item)}
                            onContextMenu={(x, y) => setContextMenu({ x, y, item })}
                        />
                    )
                })}
            </div>

            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={buildMenuItems(contextMenu.item)}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </div>
    )
}

type CardProps = {
    item: HistoryItem
    pct: number
    onClick: () => void
    onContextMenu: (x: number, y: number) => void
}

const CwCard = ({ item, pct, onClick, onContextMenu }: CardProps) => {
    const longPressTimer = useRef<number | null>(null)

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault()
        onContextMenu(e.clientX, e.clientY)
    }

    const onTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0]
        const x = touch.clientX
        const y = touch.clientY
        longPressTimer.current = window.setTimeout(() => {
            longPressTimer.current = null
            onContextMenu(x, y)
        }, 500)
    }

    const cancelLongPress = () => {
        if (longPressTimer.current !== null) {
            window.clearTimeout(longPressTimer.current)
            longPressTimer.current = null
        }
    }

    return (
        <div
            className="cw-card"
            onClick={onClick}
            onContextMenu={handleContextMenu}
            onTouchStart={onTouchStart}
            onTouchEnd={cancelLongPress}
            onTouchMove={cancelLongPress}
        >
            <div
                className="cw-thumb"
                style={{ backgroundImage: `url(${item.file.paths.thumbnail})` }}
            >
                <div className="cw-bar">
                    <div className="cw-bar-fill" style={{ width: `${pct}%` }}/>
                </div>
            </div>
            <div className="cw-name">{item.file.name}</div>
        </div>
    )
}
